import React from 'react';
import styles from './Chip.module.css';

export type ChipTone = 'neutral' | 'success' | 'warning' | 'info' | 'error';

/**
 * Map a legacy MUI `Chip` `color` prop (`default`/`primary`/`info`/`success`/
 * `warning`/`error`/…) to a design-system `ChipTone`, for pages migrating off
 * raw MUI chips that derive the colour from a helper.
 */
export const toneFromMuiColor = (color: string): ChipTone => {
  switch (color) {
    case 'success': return 'success';
    case 'warning': return 'warning';
    case 'error': return 'error';
    case 'info':
    case 'primary': return 'info';
    default: return 'neutral';
  }
};

interface ChipProps {
  children: React.ReactNode;
  /** Palette tone — neutral (default), success (green/paid), warning (amber), info (blue). */
  tone?: ChipTone;
  /** When set, the chip renders as an external link (new tab). */
  href?: string;
  /** Mainly for `e.stopPropagation()` when the chip sits inside a clickable row. */
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
}

/**
 * Design-system chip/badge (sm4rt-web-ui skill §5) — a tinted pill. Replaces
 * raw MUI `<Chip>` in app surfaces so chips read consistently. Pass `href` to
 * make it a link.
 */
export const Chip: React.FC<ChipProps> = ({ children, tone = 'neutral', href, onClick, title }) => {
  const className = `${styles.chip} ${styles[tone]}${href || onClick ? ` ${styles.clickable}` : ''}`;
  if (href) {
    return (
      <a
        className={className}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }
  return (
    <span className={className} title={title} onClick={onClick}>
      {children}
    </span>
  );
};
